import { colors, sensorUnits } from './constants';
import i18n from './i18n';
import { EnumStatusColors, Rating, SensorTypes } from './models/commonEnums';
import { ThresholdRange } from './models/commonTypeScript';

/** Light and Noise: no Good/Fair/Poor labels in IAQ2 report. */
export const SENSORS_WITHOUT_RATING_LABELS: SensorTypes[] = [SensorTypes.light, SensorTypes.sla, SensorTypes.noise];

export const sensorShowsRatingLabels = (sensor: string): boolean =>
    !SENSORS_WITHOUT_RATING_LABELS.includes(sensor as SensorTypes);

export const sensorRating = (ratings: ThresholdRange[], value: number | undefined): undefined | Rating => {
    if (value === undefined) return undefined;
    const valueInRange = ratings.find(rating => {
        const lessThanTo = rating.to ? rating.to > value : true;
        const moreThanFrom = rating.from ? rating.from <= value : true;
        return lessThanTo && moreThanFrom;
    });
    return valueInRange && valueInRange.rating;
};

export const statusColor = (ranges: ThresholdRange[], value: number | undefined): EnumStatusColors => {
    const valueRange: Rating | undefined = sensorRating(ranges, value);
    let color = EnumStatusColors.grey;
    if (valueRange === Rating.POOR) color = EnumStatusColors.red;
    else if (valueRange === Rating.FAIR) color = EnumStatusColors.yellow;
    else if (valueRange === Rating.GOOD) color = EnumStatusColors.green;
    return color;
};

export const formatThresholdNumber = (n: number): string => parseFloat(n.toFixed(2)).toString();

const getRangeDisplayUnit = (unit: keyof typeof sensorUnits): string => {
    if (unit === 'c') return `${sensorUnits[unit]}C`;
    if (unit === 'f') return `${sensorUnits[unit]}F`;
    return sensorUnits[unit];
};

export const formatRangeText = (range: ThresholdRange, unit: keyof typeof sensorUnits): string => {
    const displayUnit = getRangeDisplayUnit(unit);
    if (range.from !== undefined && range.to !== undefined) {
        return `${formatThresholdNumber(range.from)} - ${formatThresholdNumber(range.to)} ${displayUnit}`;
    }
    if (range.from !== undefined && range.to === undefined) {
        return `${i18n.t('Above')} ${formatThresholdNumber(range.from)} ${displayUnit}`;
    }
    if (range.to !== undefined && range.from === undefined) {
        return `${i18n.t('Below')} ${formatThresholdNumber(range.to)} ${displayUnit}`;
    }
    return '';
};

export const formatRating = (rating: Rating): string => {
    return rating.charAt(0) + rating.slice(1).toLowerCase();
};

export const getStatusColor = (rating: Rating): string => {
    if (rating === Rating.FAIR) return colors.yellowSunlight;
    if (rating === Rating.POOR) return colors.redPersian;
    return colors.greenBiosphere;
};

export type SpacesByRating<T = number> = {
    [Rating.POOR]: T[];
    [Rating.FAIR]: T[];
    [Rating.GOOD]: T[];
};

export type SpacesWithIssues = {
    poor: number;
    fair: number;
    good: number;
};

export type PeakRange = { min: number; max: number } | null;

export type PeakRanges = {
    poor: PeakRange;
    fair: PeakRange;
    good: PeakRange;
};

export interface DeviceMeasurement {
    average?: number;
    highestMeasurement?: number;
    lowestMeasurement?: number;
}

// Type alias for semantic clarity in IaqInsights2 (v2) code
//
// Can we avoid this? YES - we could use DeviceMeasurement everywhere
// Why add it? Code readability and intent:
// - v1 code deals with "devices" (physical sensors)
// - v2 code deals with "spaces" (rooms that may or may not have sensors)
// - Using SpaceMeasurement in v2 makes the distinction clear to developers
// - It's a zero-cost abstraction (alias, not a new type)
export type SpaceMeasurement = DeviceMeasurement;

export const calculateSpacesByRating = (
    deviceValues: DeviceMeasurement[],
    ranges: ThresholdRange[]
): SpacesByRating => {
    const spacesByRating: SpacesByRating = {
        [Rating.POOR]: [],
        [Rating.FAIR]: [],
        [Rating.GOOD]: [],
    };

    deviceValues.forEach(device => {
        if (device.average !== undefined && device.average !== null && !Number.isNaN(device.average)) {
            const deviceRating = sensorRating(ranges, device.average);
            if (deviceRating && device.highestMeasurement !== undefined && device.highestMeasurement !== null) {
                spacesByRating[deviceRating].push(device.highestMeasurement);
            }
        }
    });

    return spacesByRating;
};

export const getSpacesWithIssues = (spacesByRating: SpacesByRating): SpacesWithIssues => ({
    poor: spacesByRating[Rating.POOR].length,
    fair: spacesByRating[Rating.FAIR].length,
    good: spacesByRating[Rating.GOOD].length,
});

export const getPeakRange = (values: number[]): PeakRange => {
    if (values.length === 0) {
        return null;
    }
    return {
        min: Math.min(...values),
        max: Math.max(...values),
    };
};

export const calculatePeakRanges = (spacesByRating: SpacesByRating): PeakRanges => ({
    poor: getPeakRange(spacesByRating[Rating.POOR]),
    fair: getPeakRange(spacesByRating[Rating.FAIR]),
    good: getPeakRange(spacesByRating[Rating.GOOD]),
});
